import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PrescribedItem } from '../../../../types'
import { selectPatient } from '../../../../features/patient/PatientContextSlice'
import type { RootState } from '../../../../app/store'
import {
  ehrLaunchEstablished,
  ehrPatientAcquisitionCleared,
  ehrPatientBound,
  ehrPatientInvalidated,
} from '../../../../features/ehr-launch/ehrPatientAcquisitionActions'

export interface DeliveryState {
  sendViaEmail: boolean
  sendViaSMS: boolean
  printPaperToken: boolean
  recipientEmail: string
  recipientMobile: string
}

export interface ScriptState extends DeliveryState {
  prescribedItemList: (PrescribedItem | undefined)[]
  selectedPrescribedItemIndex: number
  addPrescribedItemError?: string
  isPaperPrescription: boolean
  ehrReviewSlotIndexes: number[]
}

const blankScript: ScriptState = {
  prescribedItemList: [undefined],
  selectedPrescribedItemIndex: 0,
  isPaperPrescription: false,
  ehrReviewSlotIndexes: [],
  sendViaEmail: false,
  sendViaSMS: false,
  printPaperToken: true,
  recipientEmail: '',
  recipientMobile: '',
}

export const ScriptSlice = createSlice({
  name: 'newScript',
  initialState: blankScript,
  reducers: {
    fetchPrescription: (state, { payload }: PayloadAction<PrescribedItem[]>) => {
      state.prescribedItemList = payload
      state.ehrReviewSlotIndexes = []
    },
    addPrescribedItem: (
      state,
      { payload: newPrescribedItem }: PayloadAction<PrescribedItem | undefined>
    ) => {
      state.prescribedItemList.push(newPrescribedItem)
      state.selectedPrescribedItemIndex = state.prescribedItemList.length - 1
    },
    selectPrescribedItem: (state, { payload: index }: PayloadAction<number>) => {
      state.selectedPrescribedItemIndex = index
    },
    updatePrescribedItem: (
      state,
      {
        payload: { index, updatedItem },
      }: PayloadAction<{
        index: number
        updatedItem: PrescribedItem
      }>
    ) => {
      if (
        state.ehrReviewSlotIndexes.includes(index) &&
        state.prescribedItemList[index]?.productId !== updatedItem.productId
      ) {
        state.ehrReviewSlotIndexes = state.ehrReviewSlotIndexes.filter((value) => value !== index)
      }
      state.prescribedItemList[index] = updatedItem
    },
    commitEhrPrescribedItem: (
      state,
      { payload }: PayloadAction<{ index: number; item: PrescribedItem }>
    ) => {
      state.prescribedItemList[payload.index] = payload.item
      state.selectedPrescribedItemIndex = payload.index
      if (!state.ehrReviewSlotIndexes.includes(payload.index)) {
        state.ehrReviewSlotIndexes.push(payload.index)
      }
    },
    deletePrescribedItem: (state, { payload: index }: PayloadAction<number>) => {
      state.prescribedItemList.splice(index, 1)
      state.ehrReviewSlotIndexes = state.ehrReviewSlotIndexes
        .filter((value) => value !== index)
        .map((value) => (value > index ? value - 1 : value))
    },
    resetPrescribedItemByIndex: (state, { payload: index }: PayloadAction<number>) => {
      state.prescribedItemList[index] = undefined
      state.ehrReviewSlotIndexes = state.ehrReviewSlotIndexes.filter((value) => value !== index)
    },
    cleanPrescribedItems: (state) => {
      const validEntries = state.prescribedItemList
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => item !== undefined)
      const validPrescribedItems = validEntries.map(({ item }) => item as PrescribedItem)
      state.ehrReviewSlotIndexes = validEntries
        .map(({ index }, nextIndex) =>
          state.ehrReviewSlotIndexes.includes(index) ? nextIndex : -1
        )
        .filter((index) => index >= 0)
      state.prescribedItemList =
        validPrescribedItems.length > 0 ? validPrescribedItems : [undefined]
      state.selectedPrescribedItemIndex = 0
    },
    clearEhrReviewCues: (state) => {
      state.ehrReviewSlotIndexes = []
    },
    setDeliveryState: (state, { payload }: PayloadAction<Partial<DeliveryState>>) => {
      return { ...state, ...payload }
    },
    setScriptState: (state, { payload }: PayloadAction<Partial<ScriptState>>) => {
      return { ...state, ...payload }
    },
    resetScript: () => {
      // Return a fresh copy of blankScript to avoid mutating the shared reference
      return {
        ...blankScript,
        prescribedItemList: [undefined],
      }
    },
    setIsPaperPrescription: (state, { payload: isPaperPrescription }: PayloadAction<boolean>) => {
      state.isPaperPrescription = isPaperPrescription
    },

    uncheckIsPaperPrescription: (state) => {
      state.isPaperPrescription = false
    },
    setAddPrescribedItemError: (state, { payload }: PayloadAction<string | undefined>) => {
      state.addPrescribedItemError = payload
    },
  },
  extraReducers(builder) {
    builder
      .addCase(selectPatient, (state, { payload: patientDetails }) => {
        state.isPaperPrescription = patientDetails?.paper_only ?? false
      })
      .addCase(ehrPatientBound, (state, { payload }) => {
        state.isPaperPrescription = payload.patient.paper_only ?? false
      })
      .addCase(ehrPatientInvalidated, () => ({
        ...blankScript,
        prescribedItemList: [undefined],
      }))
      .addCase(ehrPatientAcquisitionCleared, () => ({
        ...blankScript,
        prescribedItemList: [undefined],
      }))
      .addCase(ehrLaunchEstablished, (state, { payload }) => {
        if (!payload.reset) return
        return { ...blankScript, prescribedItemList: [undefined] }
      })
  },
})

export const {
  fetchPrescription,
  addPrescribedItem,
  selectPrescribedItem,
  updatePrescribedItem,
  deletePrescribedItem,
  resetPrescribedItemByIndex,
  setDeliveryState,
  setScriptState,
  resetScript,
  setIsPaperPrescription,
  uncheckIsPaperPrescription,
  setAddPrescribedItemError,
  cleanPrescribedItems,
  commitEhrPrescribedItem,
  clearEhrReviewCues,
} = ScriptSlice.actions

export default ScriptSlice.reducer

export const definedPrescribedItemsSelector = createSelector(
  [(state: RootState) => state.newScript.prescribedItemList],
  (items): PrescribedItem[] => items.filter((item): item is PrescribedItem => item !== undefined)
)
